import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { Loader } from 'components/Loader/Loader';
import ControlSortList from '../../components/ControlSortList/ControlSortList';
import './sortList.scss';
import { Region } from '../../types/orderTypes';
import { fetchAndUpdateSortListEntries } from '../../fetch/fetchAndUpdateSortListEntries';
import { HubSortListEntry } from '../../hooks/useFetchHubSortList';
import { fetchHubSortListEntriesAll } from '../../fetch/fetchHubSortListEntriesAll';
import _ from 'lodash';
import {
  useDeepCompareCallback,
  useDeepCompareEffect,
  useDeepCompareMemo,
} from 'use-deep-compare';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from '../../utils/pusher';

const throttledDispatch = _.throttle(
  async (
    setTemporaryListOfOrdersToUpdate: Dispatch<SetStateAction<string[]>>,
    ordersIdToUpdate: string[],
    clearIds: (bundleIds: string[]) => void
  ) => {
    setTemporaryListOfOrdersToUpdate(ordersIdToUpdate);
    clearIds(ordersIdToUpdate);
  },
  10000,
  { leading: false }
);

export function subscribeToPusher(
  setTemporaryListOfOrdersToUpdate: Dispatch<SetStateAction<string[]>>
): void {
  let sortListOrdersToUpdate = [];
  subscribeToChannel('admin-hubscans', PusherEvent.HUBSORT_UPDATE, event => {
    const [orderId] = event;
    const clearIds = (updatedIds: string[]) => {
      if (sortListOrdersToUpdate.length === updatedIds.length) {
        sortListOrdersToUpdate = [];
      } else {
        sortListOrdersToUpdate = sortListOrdersToUpdate.filter(
          id => !updatedIds.includes(id)
        );
      }
    };
    sortListOrdersToUpdate.push(orderId);
    throttledDispatch(
      setTemporaryListOfOrdersToUpdate,
      sortListOrdersToUpdate,
      clearIds
    );
  });
}

export default function SortList({ region }: { region: Region }): JSX.Element {
  const refToCurrentSortListEntries = useRef<HubSortListEntry[]>(null);
  const [loaderVisible, toggleLoader] = useState<boolean>(false);
  const [sortListEntries, setSortListEntries] = useState<HubSortListEntry[]>(
    []
  );
  const [temporaryListOfOrdersToUpdate, setTemporaryListOfOrdersToUpdate] =
    useState<string[]>([]);

  const currentBundlesId = sortListEntries
    ? sortListEntries.map(bundle => bundle.bundleId)
    : [];

  const memoizedCurrentBundlesId = useDeepCompareMemo(() => {
    return currentBundlesId;
  }, [currentBundlesId]);

  const subscribeToPusherCallback = useCallback(
    () => subscribeToPusher(setTemporaryListOfOrdersToUpdate),
    []
  );

  const fetchHubSortList = useCallback(async () => {
    toggleLoader(true);
    const [fetchedHubSortList] = await fetchHubSortListEntriesAll(region);
    setSortListEntries(fetchedHubSortList);
  }, [region]);

  const updateCurrentSortListBundles = useCallback(
    (updatedBundles: HubSortListEntry[]) => {
      const copyOfCurrentHubSortList = [...sortListEntries];
      updatedBundles.forEach(updatedBundle => {
        const foundIndex = copyOfCurrentHubSortList.findIndex(
          currentHubSortBundle =>
            currentHubSortBundle.bundleId === updatedBundle.bundleId
        );
        if (foundIndex !== -1) {
          copyOfCurrentHubSortList[foundIndex] = updatedBundle;
        }
      });
      return copyOfCurrentHubSortList.filter(
        bundle => bundle.orders.length !== 0
      );
    },
    [sortListEntries]
  );

  const updateHubSortListEntries = useCallback(
    async (bundlesId: string[]) =>
      setSortListEntries(
        updateCurrentSortListBundles(
          await fetchAndUpdateSortListEntries(bundlesId)
        )
      ),
    [updateCurrentSortListBundles]
  );

  const performSearchAndUpdateBundles = useDeepCompareCallback(async () => {
    const bundlesToUpdate = [];
    const copyOfTemporaryOrders = [...temporaryListOfOrdersToUpdate];
    setTemporaryListOfOrdersToUpdate([]);
    copyOfTemporaryOrders.forEach(orderId => {
      const filteredBundle = refToCurrentSortListEntries.current.filter(
        bundle =>
          bundle.orders.findIndex(order => order.order.id === orderId) !== -1
      );
      if (
        filteredBundle.length === 1 &&
        memoizedCurrentBundlesId.includes(filteredBundle[0].bundleId)
      ) {
        console.log('id to update', orderId);
        bundlesToUpdate.push(filteredBundle[0].bundleId);
      }
    });
    if (bundlesToUpdate.length) {
      setSortListEntries(
        updateCurrentSortListBundles(
          await fetchAndUpdateSortListEntries(bundlesToUpdate)
        )
      );
    }
  }, [
    memoizedCurrentBundlesId,
    temporaryListOfOrdersToUpdate,
    updateCurrentSortListBundles,
  ]);

  useEffect(() => {
    fetchHubSortList()
      .then(() => {
        console.log('Sort list fetched');
        toggleLoader(false);
      })
      .catch(error => console.log(error));
  }, [fetchHubSortList]);

  useDeepCompareEffect(() => {
    refToCurrentSortListEntries.current = sortListEntries;
  }, [sortListEntries]);

  useDeepCompareEffect(() => {
    if (temporaryListOfOrdersToUpdate.length) {
      console.log(temporaryListOfOrdersToUpdate);
      performSearchAndUpdateBundles()
        .then(() => console.log('Sort list updated'))
        .catch(error => console.log(error));
    }
  }, [performSearchAndUpdateBundles, temporaryListOfOrdersToUpdate]);

  useEffect(() => {
    subscribeToPusherCallback();
    return () => {
      unsubscribeFromChannel('admin-hubscans');
    };
  }, [fetchHubSortList, subscribeToPusherCallback]);

  let mainContent;
  if (loaderVisible) {
    mainContent = (
      <div className="loader-wrapper">
        <Loader type="spinner" size="xxl" />
      </div>
    );
  } else if (sortListEntries.length) {
    mainContent = (
      <div className="sort-list">
        <ControlSortList
          scanningItems={sortListEntries}
          updateHubSortListEntries={updateHubSortListEntries}
          region={region}
        />
      </div>
    );
  } else {
    mainContent = null;
  }
  return <>{mainContent}</>;
}
